/**
 * Szolgáltatások lista -- CSS marquee + pointer drag override.
 *
 * A track CSS @keyframes-szel csúszik 0 → -50%. A JS dolga:
 *   1. duration kiszámítása a track szélességéből + speed-ből (px/s)
 *   2. pointerdown → animáció pause, jelenlegi translate kiolvasása,
 *      `--dragging` class hozzáadása
 *   3. pointermove → inline `translateX` frissítése modulo (-trackWidth/2, 0]
 *   4. pointerup → `animation-delay` újraszámítása úgy, hogy a most
 *      lévő pozícióból folytatódjon az animáció (negatív delay)
 *   5. drag esetén az ezt követő `click` event-et leblokkoljuk
 *      (különben a kártya navigálna a drag után)
 *
 * Resize: a track szélessége változhat, ezért ResizeObserver-rel
 * újraszámolja a duration-t (debounced rAF-fel).
 *
 * Defer-betöltés miatt a DCL-re várunk (lásd velemenyek.js komment).
 */
(function () {
  'use strict';

  // Minimális mozgásküszöb px-ben: ha a felhasználó ennél kisebbet húz,
  // azt kattintásnak vesszük (és NEM nyeljük el a click-et).
  var DRAG_THRESHOLD = 5;

  function init() {
    var marquees = document.querySelectorAll('[data-szl-marquee]');
    marquees.forEach(setupMarquee);
  }

  function setupMarquee(root) {
    var track = root.querySelector('.szolgaltatasok-lista__track');
    if (!track) { return; }

    var speed     = parseFloat(root.getAttribute('data-szl-speed')) || 60; // px/s
    var halfWidth = 0;
    var duration  = 40; // s -- később újraszámolva

    var isPointerDown = false;
    var isDragging    = false;
    var startX        = 0;
    var startTranslate = 0;
    var currentTranslate = 0;
    var activePointerId = null;
    var resizeRaf = 0;

    /* ---------- helpers ---------- */

    function readTranslateX(el) {
      var s = window.getComputedStyle(el);
      var t = s.transform || s.webkitTransform;
      if (!t || t === 'none') { return 0; }
      // matrix(a, b, c, d, tx, ty)  |  matrix3d(..., tx, ty, tz, 1)
      var m = t.match(/matrix(?:3d)?\(([^)]+)\)/);
      if (!m) { return 0; }
      var parts = m[1].split(',').map(function (p) { return parseFloat(p); });
      if (parts.length === 6) { return parts[4]; }
      if (parts.length === 16) { return parts[12]; }
      return 0;
    }

    function wrap(t) {
      // Cél tartomány: (-halfWidth, 0]
      if (halfWidth <= 0) { return t; }
      t = t % halfWidth;
      if (t > 0) { t -= halfWidth; }
      return t;
    }

    function recalcDuration() {
      // scrollWidth = track teljes szélessége (duplikálás miatt 2× az eredeti)
      var sw = track.scrollWidth;
      halfWidth = sw / 2;
      duration = (halfWidth / speed);
      if (!isFinite(duration) || duration <= 0) { duration = 40; }
      // Csak akkor frissítjük az animation-t, ha NEM húzunk épp
      if (!isDragging) {
        track.style.animationDuration = duration + 's';
      }
    }

    function pauseAnimation() {
      currentTranslate = wrap(readTranslateX(track));
      track.style.animation = 'none';
      track.style.transform = 'translate3d(' + currentTranslate + 'px, 0, 0)';
    }

    function resumeAnimationAt(t) {
      // animation translateX: 0 → -halfWidth, duration alatt
      // adott translateX = t (t ∈ (-halfWidth, 0]) → eltelt idő = -t/halfWidth * duration
      var elapsed = (-t / halfWidth) * duration;
      track.style.transform = '';                  // engedjük át a vezérlést az animation-nek
      track.style.animation = '';                  // visszaadjuk a CSS stylesheet rule-t
      track.style.animationDuration = duration + 's';
      track.style.animationDelay = (-elapsed) + 's';
    }

    /* ---------- pointer events ---------- */

    function onPointerDown(e) {
      // csak elsődleges gomb (egér: bal; touch: bármi)
      if (e.button !== undefined && e.button !== 0) { return; }
      if (activePointerId !== null) { return; }

      isPointerDown   = true;
      isDragging      = false;
      startX          = e.clientX;
      activePointerId = e.pointerId;

      pauseAnimation();
      startTranslate = currentTranslate;
    }

    function onPointerMove(e) {
      if (!isPointerDown) { return; }
      if (e.pointerId !== activePointerId) { return; }

      var dx = e.clientX - startX;

      if (!isDragging && Math.abs(dx) > DRAG_THRESHOLD) {
        isDragging = true;
        root.classList.add('szolgaltatasok-lista__marquee--dragging');
        try { root.setPointerCapture(activePointerId); } catch (_) {}
      }

      if (!isDragging) { return; }

      var t = wrap(startTranslate + dx);
      currentTranslate = t;
      track.style.transform = 'translate3d(' + t + 'px, 0, 0)';
      e.preventDefault();
    }

    function endDrag(e) {
      if (!isPointerDown) { return; }
      if (e && e.pointerId !== activePointerId) { return; }

      isPointerDown = false;
      activePointerId = null;

      if (isDragging) {
        root.classList.remove('szolgaltatasok-lista__marquee--dragging');
        resumeAnimationAt(currentTranslate);
      } else {
        // Nem volt húzás → simán visszaadjuk az animation vezérlést
        // a meglévő pozícióból
        resumeAnimationAt(currentTranslate);
      }
    }

    function onClickCapture(e) {
      // Drag UTÁN érkező click esemény elnyelése: ne navigáljon a kártya
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    /* ---------- registration ---------- */

    root.addEventListener('pointerdown', onPointerDown);
    root.addEventListener('pointermove', onPointerMove);
    root.addEventListener('pointerup', endDrag);
    root.addEventListener('pointercancel', endDrag);
    // Capture fázisban: a kártya click-jét tudjuk blokkolni mielőtt navigálna
    root.addEventListener('click', onClickCapture, true);

    // Resize: újraszámítjuk a duration-t (rAF debounce)
    function onResize() {
      if (resizeRaf) { cancelAnimationFrame(resizeRaf); }
      resizeRaf = requestAnimationFrame(function () {
        resizeRaf = 0;
        recalcDuration();
      });
    }

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(onResize);
      ro.observe(track);
    } else {
      window.addEventListener('resize', onResize, { passive: true });
    }

    // Kép load után a track szélesség változhat (lazy-load) → recalc
    var imgs = track.querySelectorAll('img');
    imgs.forEach(function (img) {
      if (!img.complete) {
        img.addEventListener('load', onResize, { once: true });
      }
    });

    // Initial duration calc
    recalcDuration();
  }

  if (document.readyState === 'complete') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
